"use client"
import { useCallback, useEffect, useLayoutEffect, useState } from "react"
import { useRouter } from "next/navigation"

export type SessionUser = {
  id: string
  name: string | null
  email: string | null
  phone: string | null
  role: string
  isSuperAdmin?: boolean
}

// The inline script in app/layout.tsx reads these same two keys before hydration
// (to hide the logged-out navbar buttons). Change them in both places.
const LS_KEY = "ze_user"
const LS_EXPIRY = "ze_session_expiry"

function getCached(): SessionUser | null {
  try {
    const stored = localStorage.getItem(LS_KEY)
    const expiry = localStorage.getItem(LS_EXPIRY)
    if (stored && expiry && new Date(expiry) > new Date()) return JSON.parse(stored)
  } catch {}
  return null
}

export function writeCache(user: SessionUser) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(user))
    localStorage.setItem(LS_EXPIRY, new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
  } catch {}
}

export function clearCache() {
  try {
    localStorage.removeItem(LS_KEY)
    localStorage.removeItem(LS_EXPIRY)
    localStorage.removeItem("ze_otp_last")
  } catch {}
}

/**
 * useSession, the JWT cookie is the source of truth, validated server-side.
 * localStorage is a performance cache only (shows the signed-in UI before the
 * session check returns).
 *
 * On every mount it calls /api/auth/session to verify the cookie.
 * If valid → repopulates localStorage with fresh data.
 * If invalid → clears localStorage and optionally redirects.
 *
 * This fixes the cross-device phantom-login bug: a user on a new device
 * has an empty localStorage but a valid session cookie, so the server
 * call returns valid=true and we populate localStorage from it.
 */
export function useSession(opts?: { required?: boolean; redirectTo?: string }) {
  const router = useRouter()
  // Start signed out on the client too. The server has no localStorage, so it
  // always renders user = null; reading the cache in the initializer made the
  // first client render differ from the server HTML (React #418 on every page
  // after the first, and React threw the SSR HTML away).
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Apply the cache right after mount. A layout effect re-renders before the
  // browser paints, so the signed-out state is never shown.
  useLayoutEffect(() => {
    const cached = getCached()
    // Deliberate: localStorage only exists after hydration, so this cannot be initial state.
    if (cached) setUser(cached) // eslint-disable-line react-hooks/set-state-in-effect
  }, [])

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/session", { cache: "no-store" })
      const data = await res.json()
      if (data.valid && data.user) {
        setUser(data.user)
        writeCache(data.user)
        // NOTE: do NOT dispatch ze:auth here, that would create an infinite loop
        // because Navbar listens to ze:auth and calls refresh(), which would
        // dispatch ze:auth again. Login/register pages dispatch ze:auth directly.
      } else {
        setUser(null)
        clearCache()
        if (opts?.required) router.push(opts.redirectTo ?? "/auth/login")
      }
    } catch {
      // Network error, keep the cached value, don't redirect
      const cached = getCached()
      setUser(cached)
      if (!cached && opts?.required) router.push(opts.redirectTo ?? "/auth/login")
    } finally {
      setLoading(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh()

    // Re-validate when the tab regains focus (catches token expiry while away)
    const onFocus = () => refresh()
    window.addEventListener("focus", onFocus)
    return () => window.removeEventListener("focus", onFocus)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { user, loading, refresh }
}
